import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '../button'
import { Gate } from '../gate'
import { Spinner } from '../spinner'
import { CustomSelect } from '../custom-select'
import { useToast } from '../toast'
import { ConfirmDeleteDialog } from '../confirm-delete-dialog'
import { InviteMemberModal } from './invite-member-modal'
import { memberApi } from 'src/services/members/api'
import { memberQueries } from 'src/services/members/queries'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { Member, Role } from 'src/services/members/models'
import { useAuth } from 'src/stores/auth'
import { usePermissions } from 'src/hooks/use-permissions'
import { SelectOption } from 'src/services/base-model'
import { extractErrorMessage } from 'src/utils/errors'
import { UserPlusIcon, TrashIcon } from '@heroicons/react/24/outline'

const ROLE_RANK: Record<string, number> = {
  owner: 4,
  admin: 3,
  member: 2,
  viewer: 1,
}

function getRoleRank(roleName: string): number {
  return ROLE_RANK[roleName] ?? 0
}

interface MembersSectionProps {
  orgId: string
  orgName: string
}

export const MembersSection: React.FC<MembersSectionProps> = ({ orgId, orgName }) => {
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const currentUserId = useAuth.use.userId()
  const user = useAuth.use.user()
  const activeOrgId = useAuth.use.activeOrgId()
  const { hasPermission } = usePermissions()
  const canManageMembers = hasPermission('organization.manage_members')
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { t } = useTranslation()

  const { data: members, isLoading } = useQuery(memberQueries.list(orgId))
  const { data: roles } = useQuery({
    ...memberQueries.roles(),
    enabled: canManageMembers,
  })

  const currentUserRank = useMemo(() => {
    if (!user?.memberships || !activeOrgId) return 0
    const membership = user.memberships.find((m) => m.organizationId === activeOrgId)
    if (!membership) return 0
    return getRoleRank(membership.roleName)
  }, [user, activeOrgId])

  const availableRoleOptions: SelectOption[] = useMemo(() => {
    if (!roles) return []
    return (roles as Role[])
      .filter((role) => getRoleRank(role.name) <= currentUserRank)
      .map((role) => ({ label: role.displayName, value: role.name }))
  }, [roles, currentUserRank])

  const { mutate: updateRole, isPending: isUpdatingRole } = useMutation({
    mutationFn: (data: { memberId: string; role: string }) => memberApi.csc.updateMemberRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberQueries.all() })
      toast({
        title: t('members.roleUpdated'),
        description: t('members.roleChanged'),
        type: 'success',
      })
    },
    onError: (error: unknown) => {
      toast({
        title: t('common.error'),
        description: extractErrorMessage(error, t('members.updateRoleError')),
        type: 'error',
      })
    },
  })

  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: (memberId: string) => memberApi.csc.removeMember({ memberId }),
    queryKey: memberQueries.list(orgId).queryKey,
    successMessage: t('members.memberRemoved'),
  })

  const handleRoleChange = (member: Member, values: SelectOption[]) => {
    if (values.length === 0) return
    const newRole = values[0].value
    if (newRole === member.roleName) return
    updateRole({ memberId: member.id, role: newRole })
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-text-primary">{t('members.title')}</h3>
          <p className="mt-1 text-sm text-text-placeholder">{t('members.subtitle')}</p>
        </div>
        <Gate permission="organization.manage_members">
          <Button
            variant="primary"
            onClick={() => setIsInviteOpen(true)}
            icon={<UserPlusIcon className="h-4 w-4" />}
            data-testid="invite-member-button"
          >
            {t('members.inviteMember')}
          </Button>
        </Gate>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8" data-testid="members-loading">
          <Spinner size="md" />
        </div>
      ) : !members || members.length === 0 ? (
        <div
          className="rounded-md border border-border bg-surface p-4 text-center text-sm text-text-placeholder"
          data-testid="members-empty"
        >
          {t('members.emptyState')}
        </div>
      ) : (
        <div
          className="overflow-x-auto rounded-md border border-border"
          data-testid="members-table"
        >
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-surface-hover text-xs uppercase text-text-secondary">
              <tr>
                <th className="px-4 py-3 font-medium">{t('common.name')}</th>
                <th className="px-4 py-3 font-medium">{t('members.email')}</th>
                <th className="px-4 py-3 font-medium">{t('members.role')}</th>
                {canManageMembers && (
                  <th className="px-4 py-3 font-medium">{t('common.actions')}</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-surface">
              {members.map((member) => {
                const isCurrentUser = member.userId === currentUserId
                const selectedRoleOption: SelectOption[] = availableRoleOptions.filter(
                  (o) => o.value === member.roleName,
                )
                // If the member's role isn't in available options (higher rank), show it as-is
                const roleNotInOptions = canManageMembers && selectedRoleOption.length === 0

                return (
                  <tr
                    key={member.id}
                    className={isCurrentUser ? 'bg-gray-100' : ''}
                    data-testid={`member-row-${member.id}`}
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-text-primary">
                      {member.firstName} {member.lastName}
                      {isCurrentUser && (
                        <span
                          className="ml-2 text-xs text-text-placeholder"
                          data-testid="you-label"
                        >
                          {t('members.you')}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
                      {member.email}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
                      {canManageMembers && !isCurrentUser && !roleNotInOptions ? (
                        <div className="w-40" data-testid={`role-select-${member.id}`}>
                          <CustomSelect
                            options={availableRoleOptions}
                            values={selectedRoleOption}
                            onChange={(values) => handleRoleChange(member, values)}
                            multi={false}
                            clearable={false}
                            disabled={isUpdatingRole}
                          />
                        </div>
                      ) : (
                        <span data-testid={`role-text-${member.id}`}>{member.roleDisplayName}</span>
                      )}
                    </td>
                    {canManageMembers && (
                      <td className="whitespace-nowrap px-4 py-3">
                        {!isCurrentUser && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() =>
                              requestDelete(member.id, `${member.firstName} ${member.lastName}`)
                            }
                            data-testid={`remove-member-${member.id}`}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Remove member confirmation dialog */}
      <ConfirmDeleteDialog
        {...dialogProps}
        title={t('members.removeMemberTitle')}
        message={t('members.removeMemberConfirm', { itemName: dialogProps.itemName, orgName })}
        confirmLabel={t('members.remove')}
      />

      <InviteMemberModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        orgId={orgId}
      />
    </div>
  )
}
